import type { NavigationGuardWithThis, RouteLocationRaw } from 'vue-router'
import { pick } from 'lodash-es'
import { onMounted, ref, useId, watch, onUnmounted, unref, type MaybeRef } from '@/composition/vue/compositionApi'
import { getHistoryService, getNewQueryParamsService, getRouterService } from '@/core/container/ioc'
import { useCurrentRoute } from '@/composition/useCurrentRoute'
import type { Route } from '@/services/route/types'

export const redirect = (location: RouteLocationRaw) => {
  const routerService = getRouterService()
  return routerService.push(location)
}

export const usePreviousRoute = (): RouteLocationRaw | undefined => {
  const routerService = getRouterService()
  const historyService = getHistoryService()

  const previousUrl = historyService.getRouterHistory().state.back

  if (typeof previousUrl !== 'string' || !previousUrl) {
    return undefined
  }

  const previousRoute = routerService.resolve(previousUrl)

  return previousRoute?.name ? previousRoute : undefined
}

export const useGoToBackAction = (fallbackRouteRef: MaybeRef<RouteLocationRaw | undefined>) => {
  const routerService = getRouterService()
  const previousRoute = usePreviousRoute()

  return () => {
    const fallbackRoute = unref(fallbackRouteRef)

    if (previousRoute) {
      routerService.back()
    } else if (fallbackRoute) {
      routerService.replace(fallbackRoute)
    }
  }
}

export const useRouterBeforeEach = (guard: NavigationGuardWithThis<any>) => {
  const cancel = getRouterService().getRouter().beforeEach(guard)
  onUnmounted(() => {
    cancel()
  })
}

export const useExactPreviousRoute = () => {
  const currentRoute = useCurrentRoute()
  const previousRoute = ref<Route | undefined>(undefined)

  watch(
    currentRoute,
    () => {
      previousRoute.value = getRouterService().getPreviousRoute()
    },
    { immediate: true },
  )

  return previousRoute
}

export const watchQuery = <const T extends string[]>(
  paramKeys: T,
  cb: (value: Record<T[number], string | undefined>) => void,
) => {
  const queryParamsService = getNewQueryParamsService()
  const key = useId()

  const detectQueryChange = (route: { query: Record<string, unknown> }) => {
    const query = pick(route.query, paramKeys) as Record<T[number], string | undefined>
    cb(query)
  }

  queryParamsService.subscribe(key, detectQueryChange)

  const unsubscribe = () => queryParamsService.unsubscribe(key)
  onUnmounted(unsubscribe)

  onMounted(() => {
    detectQueryChange({ query: queryParamsService.getQueryParams() })
  })

  return unsubscribe
}
